import React from 'react';
import { formatCurrency, formatPercent, formatMoney, signClass } from '../../hooks/useResultsAnalysis';

function FlipAnalysis({ analysis }) {
  const {
    purchasePrice, monthlyPayment,
    renovationCost, monthlyCarry, holdingPeriod, totalHoldingCosts,
    expectedSellingPrice, sellingCosts, totalInvestment, netProfit,
    roi, annualizedROI, annualizationMonths, closingCosts, interestRate,
    interestDuringHold, otherHoldingCosts, principalRepaidDuringHold, sellingCostPercent,
    flipFinancing, flipLoanAmount, flipPoints, flipCashToClose,
  } = analysis;
  const hardMoney = flipFinancing === 'hardMoney';
  const financingLabel = { hardMoney: 'Hard-money loan (interest only)', cash: 'All cash', same: 'Long-term loan' }[flipFinancing];

  return (
    <div className="mb-8 border border-surface-border rounded bg-surface-primary overflow-hidden">
      <div className="bg-surface-elevated border-b border-surface-border px-6 py-4">
        <h2 className="text-xl font-semibold text-terminal-cyan">Fix & Flip Strategy Detailed Analysis</h2>
        <p className="text-sm text-terminal-cyan">Where the money goes, and what comes back</p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Financial Summary */}
          <div className="bg-surface-primary border rounded p-5">
            <h3 className="text-lg font-medium text-txt-primary mb-4">Financial Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Purchase Price</span>
                <span className="font-medium">${formatCurrency(purchasePrice)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Financing</span>
                <span className="font-medium">{financingLabel}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Loan Amount</span>
                <span className="font-medium">${formatCurrency(flipLoanAmount)}</span>
              </div>
              {hardMoney && (
                <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                  <span className="text-txt-secondary">Loan Points</span>
                  <span className="font-medium">${formatCurrency(flipPoints)}</span>
                </div>
              )}
              {flipFinancing === 'same' && (
                <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                  <span className="text-txt-secondary">Monthly Payment ({interestRate}%)</span>
                  <span className="font-medium">${formatCurrency(monthlyPayment)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Cash to Close</span>
                <span className="font-medium">${formatCurrency(flipCashToClose)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Renovation Cost</span>
                <span className="font-medium">${formatCurrency(renovationCost)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Monthly Expenses</span>
                <span className="font-medium">${formatCurrency(monthlyCarry)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Holding Period</span>
                <span className="font-medium">{holdingPeriod} months</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Loan Interest During Hold</span>
                <span className="font-medium">${formatCurrency(interestDuringHold)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Monthly Expenses During Hold</span>
                <span className="font-medium">${formatCurrency(otherHoldingCosts)}</span>
              </div>
              <div className="flex justify-between items-center font-medium text-txt-primary">
                <span>Total Holding Costs</span>
                <span>${formatCurrency(totalHoldingCosts)}</span>
              </div>
            </div>
          </div>

          {/* Profit Calculation */}
          <div className="bg-surface-primary border rounded p-5">
            <h3 className="text-lg font-medium text-txt-primary mb-4">Profit Calculation</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">After Repair Value (ARV)</span>
                <span className="font-medium">${formatCurrency(expectedSellingPrice)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Selling Costs ({sellingCostPercent}%)</span>
                <span className="font-medium">-${formatCurrency(sellingCosts)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Purchase Price</span>
                <span className="font-medium">-${formatCurrency(purchasePrice)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Renovation Cost</span>
                <span className="font-medium">-${formatCurrency(renovationCost)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Purchase Closing Costs</span>
                <span className="font-medium">-${formatCurrency(closingCosts)}</span>
              </div>
              {flipPoints > 0 && (
                <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                  <span className="text-txt-secondary">Loan Points</span>
                  <span className="font-medium">-${formatCurrency(flipPoints)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Holding Costs</span>
                <span className="font-medium">-${formatCurrency(totalHoldingCosts)}</span>
              </div>
              <div className={`flex justify-between items-center font-medium pt-2 ${signClass(netProfit)}`}>
                <span>Net Profit</span>
                <span>{formatMoney(netProfit)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Key Performance Metrics */}
        <div className="bg-surface-primary border rounded p-5 mb-6">
          <h3 className="text-lg font-medium text-txt-primary mb-4">Key Performance Metrics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-cyan mb-1">Total Investment</p>
              <p className="text-2xl font-bold text-terminal-cyan">${formatCurrency(totalInvestment)}</p>
            </div>
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className={`text-sm mb-1 ${signClass(netProfit)}`}>Net Profit</p>
              <p className={`text-2xl font-bold ${signClass(netProfit)}`}>{formatMoney(netProfit)}</p>
            </div>
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-cyan mb-1">Return on Investment</p>
              <p className="text-2xl font-bold text-terminal-cyan">{formatPercent(roi)}%</p>
            </div>
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-amber mb-1">Annualized ROI</p>
              <p className="text-2xl font-bold text-terminal-amber">{formatPercent(annualizedROI)}%</p>
            </div>
          </div>
        </div>

        {/* Understanding the Math */}
        <div className="bg-surface-primary border rounded p-5">
          <h3 className="text-lg font-medium text-txt-primary mb-4">Understanding the Math</h3>

          <div className="space-y-4 text-sm text-txt-secondary">
            <p>
              <strong className="text-txt-primary">Total Investment:</strong> All cash you put in before the sale: cash to close (purchase and renovation not covered by the loan, closing costs, and any loan points), holding costs, and any loan principal repaid during the hold.
            </p>
            <div className="bg-surface-elevated p-3 rounded overflow-x-auto">
              <code>Total Investment = Cash to Close + Holding Costs + Principal Repaid</code><br />
              <code>Total Investment = ${formatCurrency(flipCashToClose)} + ${formatCurrency(totalHoldingCosts)} + ${formatCurrency(principalRepaidDuringHold)} = ${formatCurrency(totalInvestment)}</code>
            </div>

            <p>
              <strong className="text-txt-primary">Net Profit:</strong> The selling price less selling costs and every expense. Holding costs are loan interest plus your monthly expenses; principal repaid is not a cost because it comes back at sale. Income tax on the profit is not included.
            </p>
            <div className="bg-surface-elevated p-3 rounded overflow-x-auto">
              <code>Net Profit = ARV - Selling Costs - Purchase Price - Renovation - Closing Costs - Loan Points - Holding Costs</code><br />
              <code>Net Profit = ${formatCurrency(expectedSellingPrice)} - ${formatCurrency(sellingCosts)} - ${formatCurrency(purchasePrice)} - ${formatCurrency(renovationCost)} - ${formatCurrency(closingCosts)} - ${formatCurrency(flipPoints)} - ${formatCurrency(totalHoldingCosts)} = {formatMoney(netProfit)}</code>
            </div>

            <p>
              <strong className="text-txt-primary">Return on Investment (ROI):</strong> Profit as a share of what you put in.
            </p>
            <div className="bg-surface-elevated p-3 rounded">
              <code>ROI = (Net Profit / Total Investment) x 100%</code><br />
              <code>ROI = ({formatMoney(netProfit)} / ${formatCurrency(totalInvestment)}) x 100% = {formatPercent(roi)}%</code>
            </div>

            <p>
              <strong className="text-txt-primary">Annualized ROI:</strong> ROI as a yearly rate, so you can compare it with the rentals. Anything under 3 months is counted as 3.
            </p>
            <div className="bg-surface-elevated p-3 rounded overflow-x-auto">
              <code>Annualized ROI = (1 + ROI) ^ (12 / Months) - 1</code><br />
              <code>Annualized ROI = (1 + {formatPercent(roi)}%) ^ (12 / {annualizationMonths}) - 1 = {formatPercent(annualizedROI)}%</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlipAnalysis;
